import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, KeyboardAvoidingView, ScrollView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Toast from '@/components/Toast';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import PrimaryButton from '@/components/PrimaryButton';
import PageHeader from '@/components/PageHeader';

export default function ChangeEmailScreen() {
  const router = useRouter();
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2 | 'confirmation'>(1);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleClose = () => {
    router.back();
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      showToast(t('account.changePhone.errorPasswordRequired'), 'error');
      return;
    }

    setLoading(true);

    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        showToast(t('account.changeEmail.errorUserInfo'), 'error');
        return;
      }

      const response = await fetch('https://mobile.padrinho.app/api/auth/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.error === 'too_many_attempts') {
          showToast(t('account.changeEmail.errorTooManyAttempts'), 'error');
        } else {
          showToast(t('account.changeEmail.errorPassword'), 'error');
        }
        return;
      }

      setStep(2);
    } catch (error) {
      console.error('Error verifying password:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail) {
      showToast(t('account.changeEmail.emailPlaceholder'), 'error');
      return;
    }

    if (!validateEmail(newEmail)) {
      showToast(t('account.changeEmail.errorGeneric'), 'error');
      return;
    }

    if (newEmail === profile?.email) {
      showToast(t('account.changeEmail.errorGeneric'), 'error');
      return;
    }

    setLoading(true);

    try {
      const { supabase } = await import('@/lib/supabase');

      // Update email
      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (updateError) {
        if (updateError.message.toLowerCase().includes('already registered')) {
          showToast(t('account.changeEmail.errorEmailInUse'), 'error');
        } else if (updateError.message.toLowerCase().includes('rate limit') || updateError.message.toLowerCase().includes('too many requests') || updateError.message.toLowerCase().includes('email rate limit exceeded')) {
          showToast(t('account.changeEmail.errorRateLimit'), 'error');
        } else {
          showToast(t('account.changeEmail.errorGeneric') + ': ' + updateError.message, 'error');
        }
        setLoading(false);
        return;
      }

      setStep('confirmation');
      // PRD §5.10 — `settings_changed` (`field=email`). Fires when the
      // Supabase update succeeds (the user still needs to confirm via the
      // email link, but the request has been accepted).
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'email' }))
        .catch(() => {});
      showToast(t('account.changeEmail.successMessage'), 'success');

    } catch (error) {
      console.error('Error changing email:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProfile = () => {
    router.back();
  };

  if (step === 'confirmation') {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('account.changeEmail.successTitle')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />

        <View style={styles.confirmationContent}>
          <View style={styles.iconContainer}>
            <Svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <Path d="M32 0C14.327 0 0 14.327 0 32s14.327 32 32 32 32-14.327 32-32S49.673 0 32 0zm-2.344 46.031L15.219 31.594l4.437-4.437 10 10L50.344 16.469l4.437 4.437-22.125 25.125z" fill="#4CAF50"/>
            </Svg>
          </View>

          <Text style={styles.confirmationTitle}>{t('account.changeEmail.confirmationTitle')}</Text>
          <Text style={styles.confirmationMessage}>
            {t('account.changeEmail.confirmationMessage')}
          </Text>
          <Text style={styles.newEmailText}>{newEmail}</Text>
          <Text style={styles.confirmationInstructions}>
            {t('account.changeEmail.confirmationInstructions')}
          </Text>
        </View>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton 
            title={t('account.changeEmail.backToProfile')}
            onPress={handleBackToProfile}
          />
        </View>

        <Toast
          visible={toastVisible}
          type={toastType}
          message={toastMessage}
          onHide={hideToast}
          duration={4000}
        />
      </SafeAreaView>
    );
  }

  if (step === 1) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('account.changeEmail.title')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />

        <KeyboardAvoidingView 
          style={styles.keyboardAvoidingView} 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView 
            style={styles.scrollView}
            contentContainerStyle={styles.scrollViewContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.content}>
              <Text style={styles.description}>
                {t('account.changeEmail.subtitleStep1')}
              </Text>

              <View style={styles.form}>
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>{t('account.changeEmail.currentPassword')}</Text>
                  <TextInput
                    style={[styles.input, { color: '#151515' }]}
                    value={password}
                    onChangeText={setPassword}
                    autoFocus
                    placeholder={t('account.changeEmail.passwordPlaceholder')}
                    placeholderTextColor="#A19F97"
                    secureTextEntry
                    autoCapitalize="none"
                  />
                </View>
              </View>
            </View>
          </ScrollView>

          <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
            <PrimaryButton
              title={loading ? t('common.loading') : t('common.next')}
              onPress={handleVerifyPassword}
              disabled={loading || !password}
            />
          </View>
        </KeyboardAvoidingView>

        <Toast
          visible={toastVisible}
          type={toastType}
          message={toastMessage}
          onHide={hideToast}
          duration={4000}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('account.changeEmail.newEmail')} 
        onBack={() => setStep(1)}
        backgroundColor="#FFFFFF"
      />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Text style={styles.description}>
              {t('account.changeEmail.subtitle')}
            </Text>

            <View style={styles.currentEmailContainer}>
              <Text style={styles.currentEmailLabel}>{t('account.changeEmail.currentEmail')}</Text>
              <Text style={styles.currentEmail}>{user?.email || profile?.email}</Text>
            </View>

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('account.changeEmail.newEmail')}</Text>
                <TextInput
                  style={[styles.input, { color: '#151515' }]}
                  value={newEmail}
                  onChangeText={setNewEmail}
                  autoFocus
                  placeholder={t('account.changeEmail.emailPlaceholder')}
                  placeholderTextColor="#A19F97"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading ? t('common.loading') : t('account.changeEmail.button')}
            onPress={handleChangeEmail}
            disabled={loading}
          />
        </View>
      </KeyboardAvoidingView>

      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  placeholder: {
    width: 24,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  description: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    marginBottom: 24,
  },
  currentEmailContainer: {
    backgroundColor: '#F9F8F3',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  currentEmailLabel: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    marginBottom: 4,
  },
  currentEmail: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  form: {
    gap: 24,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E7EAED',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    backgroundColor: '#FFFFFF',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },

  confirmationContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  iconContainer: {
    marginBottom: 24,
  },
  confirmationTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Medium',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 16,
  },
  confirmationMessage: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    textAlign: 'center',
    marginBottom: 8,
  },
  newEmailText: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '600',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 16,
  },
  confirmationInstructions: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    textAlign: 'center',
    lineHeight: 20,
  },

});